import { Button } from "@foreday/ui/components/button";
import { buttonVariants } from "@foreday/ui/components/button-variants";
import { PlusIcon, UserPlusIcon } from "@foreday/ui/components/icons";
import { cn } from "@foreday/ui/lib/utils";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import type * as React from "react";
import { Suspense } from "react";
import { z } from "zod";
import { ToolbarActions } from "@/client/components/page";
import { HeldTitle } from "@/client/components/page-pending";
import { TableShapeSkeleton } from "@/client/components/table-skeleton";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import { useNow } from "@/client/lib/use-now";
import { useReadOnly } from "@/client/lib/use-read-only";
import { attendanceCopy } from "@/features/attendance/copy";
import { setsOthersHours } from "@/features/attendance/roles";
import { dayIn } from "@/features/attendance/rules";
import { AskForLeaveDialog } from "@/features/leave/components/ask-for-leave";
import { LeaveBalances } from "@/features/leave/components/leave-balances";
import { LeaveRequestPanel } from "@/features/leave/components/leave-request-panel";
import { LeaveFailed } from "@/features/leave/components/leave-states";
import { LeaveTab } from "@/features/leave/components/leave-tab";
import { leaveCopy } from "@/features/leave/copy";
import {
  leaveBalancesQuery,
  leaveOffQuery,
  leaveRequestsQuery,
  leaveSetupQuery,
  leaveTypesQuery,
  personLeaveQuery,
  waitingLeaveQuery,
} from "@/features/leave/leave-query";
import { useLeaveChanges } from "@/features/leave/use-leave-changes";
import { peopleQuery } from "@/features/people/people-query";
import {
  preferencesQuery,
  profileQuery,
} from "@/features/settings/settings-query";
import { leaveYearOf } from "@/shared/leave-year";

const copy = leaveCopy.page;

/** "On leave" turns on and off with the company's day, so the date is read again each minute. */
const TICK = 60_000;

/** The ask dialog and the decision panel are open while the address says so, so Back closes them. */
const leaveSearch = z.object({
  ask: z.boolean().optional().catch(undefined),
  decide: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_authenticated/$slug/attendance/leave")({
  validateSearch: leaveSearch,
  beforeLoad: ({ context }) => requireModule(context, "leave"),
  loader: async ({ context }) => {
    const { queryClient } = context;
    const [{ personId }, , , { startMonth }] = await Promise.all([
      ensureTenantData(queryClient, profileQuery),
      ensureTenantData(queryClient, preferencesQuery),
      ensureTenantData(queryClient, peopleQuery),
      ensureTenantData(queryClient, leaveSetupQuery),
      ensureTenantData(queryClient, leaveOffQuery),
      setsOthersHours(context.role)
        ? ensureTenantData(queryClient, waitingLeaveQuery)
        : null,
    ]);
    const year = leaveYearOf(
      dayIn(context.companyTimezone, new Date()),
      startMonth,
    );
    await Promise.all([
      ensureTenantData(queryClient, leaveBalancesQuery(personId)),
      ensureTenantData(queryClient, personLeaveQuery(personId, year)),
      // What the ask dialog shows the moment it opens.
      ensureTenantData(queryClient, leaveRequestsQuery(personId)),
    ]);
    return { year };
  },
  head: ({ match }) => ({
    meta: [
      {
        title: pageTitle(
          match.context.companyModules.includes("attendance")
            ? attendanceCopy.title
            : attendanceCopy.tabs.leave,
          match.context.companyName,
        ),
      },
    ],
  }),
  pendingMs: PENDING_MS,
  pendingComponent: () => (
    <>
      <LeaveActions
        balances={<HeldTitle className="text-body" lines={["w-48"]} />}
      />
      <TableShapeSkeleton />
    </>
  ),
  errorComponent: Failed,
  component: Leave,
});

/**
 * Beside the views, until the panel is wide enough for every label (the decision panel open at
 * 1440), the buttons keep only their icons and speak their labels, so the toolbar stays one row.
 * A phone gives the buttons a row of their own, so they keep their labels there.
 */
const SQUEEZED =
  "@panel-md/main:@max-panel-xl/main:w-[var(--size-touch)] @panel-md/main:@max-panel-xl/main:px-0";
const SPOKEN = "@panel-md/main:@max-panel-xl/main:sr-only";

/** The balances, and the ways to add leave, which work before the page's reads land. */
function LeaveActions({ balances }: { readonly balances: React.ReactNode }) {
  const { slug } = Route.useParams();
  const { role } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const { lock } = useReadOnly();
  return (
    // Until the panel is wide, the balances move above the list, leaving the toolbar to the buttons.
    <ToolbarActions>
      <span className="hidden @panel-xl/main:contents">{balances}</span>
      {setsOthersHours(role) ? (
        // Leave is added from the person's own page, where their balance is.
        <Link
          to="/$slug/team"
          params={{ slug }}
          className={cn(buttonVariants({ variant: "secondary" }), SQUEEZED)}
        >
          <UserPlusIcon
            aria-hidden="true"
            className="hidden @panel-md/main:@max-panel-xl/main:block"
          />
          <span className={SPOKEN}>{copy.addForSomeone}</span>
        </Link>
      ) : null}
      <Button
        className={SQUEEZED}
        onClick={() => navigate({ search: { ask: true } })}
        {...lock}
      >
        <PlusIcon aria-hidden="true" />
        <span className={SPOKEN}>{copy.ask}</span>
      </Button>
    </ToolbarActions>
  );
}

function Failed() {
  const router = useRouter();
  return <LeaveFailed onRetry={() => void router.invalidate()} />;
}

function Leave() {
  const { year } = Route.useLoaderData();
  const { ask, decide } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { role, companyTimezone } = Route.useRouteContext();
  const now = useNow(TICK);
  const today = dayIn(companyTimezone, now);
  const decider = setsOthersHours(role);
  const { data: profile } = useSuspenseQuery(profileQuery);
  const { data: preferences } = useSuspenseQuery(preferencesQuery);
  const viewerId = profile.personId;
  const { data: people } = useSuspenseQuery(peopleQuery);
  const { data: types } = useSuspenseQuery(leaveTypesQuery);
  const { data: balances } = useSuspenseQuery(leaveBalancesQuery(viewerId));
  const { data: mine } = useSuspenseQuery(personLeaveQuery(viewerId, year));
  const { data: off } = useSuspenseQuery(leaveOffQuery);
  // Read by the loader for a Manager or an Admin; an Employee may not ask.
  const { data: waiting = [] } = useQuery({
    ...waitingLeaveQuery,
    enabled: decider,
  });
  const changes = useLeaveChanges(viewerId, year);
  const typeName = (id: string) =>
    types.find((type) => type.id === id)?.name ?? "";
  const asking = (open: boolean) =>
    navigate({ search: { ask: open || undefined } });
  const deciding = (groupId: string | undefined) =>
    navigate({ search: { decide: groupId } });

  return (
    <>
      <LeaveActions
        balances={<LeaveBalances types={types} balances={balances.items} />}
      />
      <div className="empty:hidden @panel-xl/main:hidden">
        <LeaveBalances types={types} balances={balances.items} />
      </div>
      <LeaveTab
        today={today}
        viewerId={viewerId}
        mine={mine}
        off={off}
        waiting={waiting}
        people={people}
        typeName={typeName}
        onWithdraw={changes.withdraw}
        onCancel={changes.cancel}
        onDecide={(one) => deciding(one.groupId)}
      />
      {decider && decide ? (
        <Suspense>
          <LeaveRequestPanel
            key={decide}
            groupId={decide}
            people={people}
            viewerId={viewerId}
            timeZone={companyTimezone}
            onClose={() => deciding(undefined)}
          />
        </Suspense>
      ) : null}
      <AskForLeaveDialog
        open={ask === true}
        onOpenChange={asking}
        personId={viewerId}
        timeZone={companyTimezone}
        startsOn={preferences.startOfWeek}
      />
    </>
  );
}
